// Pie viz — SVG donut + legend.
//
// Column resolution: same as BarViz (panel.barLabelCol / barValueCol or
// auto-detect col 0 + first numeric). panel.rowLabelIn filters rows by
// label, also like BarViz.
//
// Slice cap: panel.pieLimit (default 8). Slices beyond the cap are
// rolled into a single "other" slice. Zero-value rows are dropped.

function isNumericCell(v) {
  if (typeof v === 'number') return Number.isFinite(v);
  if (typeof v === 'string') return /^-?\d+(\.\d+)?$/.test(v);
  return false;
}
function asNumber(v) {
  if (typeof v === 'number') return v;
  if (typeof v === 'string' && /^-?\d+(\.\d+)?$/.test(v)) return parseFloat(v);
  return 0;
}

const PALETTE = [
  '#00E5C7', '#0099FF', '#FFB454', '#A78BFA',
  '#F472B6', '#FBBF24', '#34D399', '#60A5FA', '#9CA3AF',
];

function pickColumns(columns, rows, panel) {
  if (!columns?.length) return null;
  let labelIdx = 0;
  let valueIdx = -1;
  if (panel?.barLabelCol) {
    const i = columns.indexOf(panel.barLabelCol);
    if (i >= 0) labelIdx = i;
  }
  if (panel?.barValueCol) {
    const i = columns.indexOf(panel.barValueCol);
    if (i >= 0) valueIdx = i;
  }
  if (valueIdx < 0) {
    // Scan all rows for a numeric cell — see BarViz for the SHOW DISK
    // STATUS mixed-type rationale.
    for (let i = 0; i < columns.length; i++) {
      if (i === labelIdx) continue;
      if (rows?.some(r => isNumericCell(r[i]))) { valueIdx = i; break; }
    }
  }
  if (valueIdx < 0) return null;
  return { labelIdx, valueIdx };
}

function arcPath(cx, cy, r, startAngle, endAngle) {
  // Special-case the full circle — an arc with the same start and end
  // point doesn't render. Two half-arcs cover it.
  if (endAngle - startAngle >= Math.PI * 2 - 1e-6) {
    return `M ${cx - r} ${cy} A ${r} ${r} 0 1 1 ${cx + r} ${cy} A ${r} ${r} 0 1 1 ${cx - r} ${cy} Z`;
  }
  const x1 = cx + r * Math.cos(startAngle);
  const y1 = cy + r * Math.sin(startAngle);
  const x2 = cx + r * Math.cos(endAngle);
  const y2 = cy + r * Math.sin(endAngle);
  const large = endAngle - startAngle > Math.PI ? 1 : 0;
  return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
}

export function PieViz({ columns, rows, error, loading, panel }) {
  if (error) return <div className="report-viz-empty error">{error}</div>;
  if (loading && !rows) return <div className="report-viz-empty">Loading…</div>;
  if (!rows?.length) return <div className="report-viz-empty">No rows</div>;

  const cols = pickColumns(columns, rows, panel);
  if (!cols) {
    return <div className="report-viz-empty">Pie viz needs a numeric column. None detected.</div>;
  }

  const limit = Number.isFinite(panel?.pieLimit) ? panel.pieLimit : 8;
  const filterSet = Array.isArray(panel?.rowLabelIn) && panel.rowLabelIn.length
    ? new Set(panel.rowLabelIn) : null;
  const filtered = filterSet ? rows.filter(r => filterSet.has(r[cols.labelIdx])) : rows;
  const all = filtered
    .map(r => ({ label: String(r[cols.labelIdx] ?? ''), value: asNumber(r[cols.valueIdx]) }))
    .filter(s => s.value > 0)
    .sort((a, b) => b.value - a.value);

  if (!all.length) return <div className="report-viz-empty">All values are zero.</div>;

  const top = all.slice(0, limit);
  const rest = all.slice(limit);
  const slices = rest.length
    ? [...top, { label: `other (${rest.length})`, value: rest.reduce((s, r) => s + r.value, 0) }]
    : top;

  const total = slices.reduce((s, x) => s + x.value, 0);
  const cx = 80, cy = 80, r = 70;
  let angle = -Math.PI / 2;
  const arcs = slices.map((s, i) => {
    const sweep = (s.value / total) * Math.PI * 2;
    const path = arcPath(cx, cy, r, angle, angle + sweep);
    angle += sweep;
    return { path, color: PALETTE[i % PALETTE.length], pct: s.value / total };
  });

  return (
    <div className="report-viz-pie">
      <svg viewBox="0 0 160 160" width="160" height="160" className="pie-svg">
        {arcs.map((a, i) => (
          <path key={i} d={a.path} fill={a.color} stroke="var(--app-bg-2)" strokeWidth="1" />
        ))}
        <circle cx={cx} cy={cy} r={r * 0.55} fill="var(--app-bg-2)" />
      </svg>
      <ul className="pie-legend">
        {slices.map((s, i) => (
          <li key={i}>
            <span className="pie-swatch" style={{ background: PALETTE[i % PALETTE.length] }} />
            <span className="pie-name" title={s.label}>{s.label}</span>
            <span className="pie-pct">{(arcs[i].pct * 100).toFixed(1)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
